import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import {
  useCreateEnglishQuizBlueprintMutation,
} from "../../../slices/englishQuizBlueprintApiSlice";

import {
  useGetChaptersBySubjectQuery,
} from "../../../slices/chapterApiSlice";

import BlueprintActions from "./BlueprintActions";

// ============================================================================
// Initial Values
// ============================================================================

const initialBlueprint = {
  sections: [],
  shuffleSections: false,
};

const initialSection = {
  name: "",
  order: 1,
  groupType: "",
  chapterId: "",
  selectionCount: 1,

  keepGroupTogether: true,
  randomizeGroups: true,
  randomizeQuestionsWithinGroup: false,
};

// ============================================================================
// Group Types
// ============================================================================

const GROUP_TYPES = [
  {
    value: "passage",
    label: "Passage",
  },
  {
    value: "clozeTest",
    label: "Cloze Test",
  },
  {
    value: "commonDirections",
    label: "Common Directions",
  },
  {
    value: "caselet",
    label: "Caselet",
  },
  {
    value: "paraJumble",
    label: "Para Jumble",
  },
  {
    value: "sentenceBased",
    label: "Sentence Based",
  },
  {
    value: "other",
    label: "Other",
  },
];

// ============================================================================
// Helpers
// ============================================================================

function getId(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "object") {
    return String(
      value?._id ||
        value?.id ||
        value?.value ||
        ""
    );
  }

  return String(value);
}

function getQuizId(quiz) {
  return getId(
    quiz?._id ??
      quiz?.id ??
      quiz?.quizId
  );
}

function getSubjectId(quiz) {
  return getId(
    quiz?.questionBankSubject ??
      quiz?.subject ??
      quiz?.questionBank?.subject
  );
}

function getErrorMessage(error) {
  if (!error) {
    return "Something went wrong.";
  }

  if (typeof error === "string") {
    return error;
  }

  return (
    error?.data?.message ||
    error?.data?.error ||
    error?.message ||
    "Something went wrong. Please try again."
  );
}

// ============================================================================
// Payload
// ============================================================================

function buildPayload(form) {
  return {
    sections: form.sections.map(
      (section, index) => ({
        name: section.name?.trim() || "",
        order: index + 1,

        chapter:
          section.chapterId || null,

        groupType:
          section.groupType || "",

        // IMPORTANT:
        // This is the number of GROUPS, not questions.
        selectionCount: Number(
          section.selectionCount || 1
        ),

        keepGroupTogether:
          section.keepGroupTogether === true,

        randomizeGroups:
          section.randomizeGroups === true,

        randomizeQuestionsWithinGroup:
          section.randomizeQuestionsWithinGroup === true,
      })
    ),

    shuffleSections:
      form.shuffleSections === true,
  };
}

// ============================================================================
// Component
// ============================================================================

export default function EnglishQuizBlueprintCreate({
  quizId: initialQuizId = "",
  quiz: initialQuiz = null,
  subjectId: initialSubjectId = "",
  onSuccess,
  onCancel,
  className = "",
}) {
  const location = useLocation();

  // --------------------------------------------------------------------------
  // Quiz
  // --------------------------------------------------------------------------

  const quiz =
    initialQuiz ||
    location.state?.quiz ||
    null;

  const quizId = useMemo(
    () =>
      getId(initialQuizId) ||
      getQuizId(quiz),
    [initialQuizId, quiz]
  );

  // --------------------------------------------------------------------------
  // Subject
  // --------------------------------------------------------------------------

  const subjectId = useMemo(
    () =>
      getId(initialSubjectId) ||
      getId(
        location.state?.subjectId
      ) ||
      getSubjectId(quiz),
    [
      initialSubjectId,
      location.state?.subjectId,
      quiz,
    ]
  );

  const subjectName = useMemo(() => {
    const subject =
      quiz?.questionBankSubject ??
      quiz?.subject ??
      quiz?.questionBank?.subject;

    if (
      !subject ||
      typeof subject !== "object"
    ) {
      return "English";
    }

    return (
      subject.name ||
      subject.title ||
      subject.subjectName ||
      "English"
    );
  }, [quiz]);

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  const [form, setForm] =
    useState(initialBlueprint);

  const [formError, setFormError] =
    useState("");

  const [createdBlueprint, setCreatedBlueprint] =
    useState(null);

  // --------------------------------------------------------------------------
  // Chapters
  // --------------------------------------------------------------------------

  const {
    data: chaptersResponse,
    isLoading: chaptersLoading,
    isFetching: chaptersFetching,
    error: chaptersError,
  } = useGetChaptersBySubjectQuery(
    subjectId,
    {
      skip: !subjectId,
    }
  );

  const chapters = useMemo(() => {
    if (Array.isArray(chaptersResponse)) {
      return chaptersResponse;
    }

    if (
      Array.isArray(
        chaptersResponse?.chapters
      )
    ) {
      return chaptersResponse.chapters;
    }

    if (
      Array.isArray(
        chaptersResponse?.data
      )
    ) {
      return chaptersResponse.data;
    }

    if (
      Array.isArray(
        chaptersResponse?.data?.chapters
      )
    ) {
      return chaptersResponse.data.chapters;
    }

    return [];
  }, [chaptersResponse]);

  // --------------------------------------------------------------------------
  // Create
  // --------------------------------------------------------------------------

  const [
    createBlueprint,
    {
      isLoading: creating,
    },
  ] =
    useCreateEnglishQuizBlueprintMutation();

  // --------------------------------------------------------------------------
  // Section Change
  // --------------------------------------------------------------------------

  const updateSection = (
    index,
    updates
  ) => {
    if (creating) {
      return;
    }

    setForm((current) => ({
      ...current,

      sections: current.sections.map(
        (section, sectionIndex) =>
          sectionIndex === index
            ? {
                ...section,
                ...updates,
              }
            : section
      ),
    }));

    setFormError("");
  };

  // --------------------------------------------------------------------------
  // Add Section
  // --------------------------------------------------------------------------

  const addSection = () => {
    if (creating) {
      return;
    }

    if (!subjectId) {
      setFormError(
        "Subject ID is missing."
      );
      return;
    }

    if (!chapters.length) {
      setFormError(
        "No chapters are available."
      );
      return;
    }

    setForm((current) => ({
      ...current,

      sections: [
        ...current.sections,
        {
          ...initialSection,
          order:
            current.sections.length + 1,
        },
      ],
    }));

    setFormError("");
  };

  // --------------------------------------------------------------------------
  // Remove Section
  // --------------------------------------------------------------------------

  const removeSection = (index) => {
    if (creating) {
      return;
    }

    setForm((current) => ({
      ...current,

      sections: current.sections
        .filter(
          (_, sectionIndex) =>
            sectionIndex !== index
        )
        .map(
          (section, sectionIndex) => ({
            ...section,
            order: sectionIndex + 1,
          })
        ),
    }));
  };

  // --------------------------------------------------------------------------
  // Move Section
  // --------------------------------------------------------------------------

  const moveSection = (
    index,
    direction
  ) => {
    if (creating) {
      return;
    }

    setForm((current) => {
      const sections = [
        ...current.sections,
      ];

      const targetIndex =
        direction === "up"
          ? index - 1
          : index + 1;

      if (
        targetIndex < 0 ||
        targetIndex >= sections.length
      ) {
        return current;
      }

      [
        sections[index],
        sections[targetIndex],
      ] = [
        sections[targetIndex],
        sections[index],
      ];

      return {
        ...current,

        sections: sections.map(
          (section, sectionIndex) => ({
            ...section,
            order: sectionIndex + 1,
          })
        ),
      };
    });
  };

  // --------------------------------------------------------------------------
  // Reset
  // --------------------------------------------------------------------------

  const reset = () => {
    setForm(initialBlueprint);
    setCreatedBlueprint(null);
    setFormError("");
  };

  // --------------------------------------------------------------------------
  // Create Blueprint
  // --------------------------------------------------------------------------

  const handleSave = async () => {
    setFormError("");
    setCreatedBlueprint(null);

    if (!quizId) {
      setFormError(
        "Quiz ID is required."
      );
      return;
    }

    if (!form.sections.length) {
      setFormError(
        "Add at least one section."
      );
      return;
    }

    const incompleteSection =
      form.sections.find(
        (section) =>
          !section.name?.trim() ||
          !section.chapterId ||
          !section.groupType ||
          Number(section.selectionCount) < 1
      );

    if (incompleteSection) {
      setFormError(
        "Please complete all section fields."
      );
      return;
    }

    const payload = {
      quizId,
      ...buildPayload(form),
    };

    console.log(
      "CREATE ENGLISH BLUEPRINT:",
      payload
    );

    try {
      const response =
        await createBlueprint(
          payload
        ).unwrap();

      const blueprint =
        response?.blueprint ||
        response?.data?.blueprint ||
        response?.data ||
        response;

      setCreatedBlueprint(
        blueprint
      );

      onSuccess?.({
        quiz,
        quizId,
        blueprint,
      });
    } catch (error) {
      console.error(
        "CREATE ENGLISH BLUEPRINT ERROR:",
        error
      );

      setFormError(
        getErrorMessage(error)
      );
    }
  };

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  return (
    <div
      className={`w-full space-y-6 ${className}`}
    >
      <div className="rounded-2xl border border-gray-200 bg-white">

        {/* HEADER */}

        <div className="border-b border-gray-200 px-5 py-5">
          <h1 className="text-lg font-semibold text-gray-900">
            Create English Quiz Blueprint
          </h1>

          <p className="mt-1 text-sm text-gray-500">
            Define the groups to select for
            each quiz section.
          </p>
        </div>

        <div className="space-y-6 p-5">

          {/* SUBJECT */}

          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-indigo-500">
              Subject
            </p>

            <p className="mt-1 text-sm font-semibold text-indigo-900">
              {subjectName}
            </p>

            {chaptersLoading && (
              <p className="mt-2 text-xs text-indigo-700">
                Loading chapters...
              </p>
            )}

            {chaptersError && (
              <p className="mt-2 text-xs text-red-600">
                Failed to load chapters.
              </p>
            )}
          </div>

          {/* SECTIONS */}

          <div>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  Blueprint Sections
                </h2>

                <p className="mt-1 text-xs text-gray-500">
                  Each section selects complete
                  groups.
                </p>
              </div>

              <button
                type="button"
                onClick={addSection}
                disabled={
                  creating ||
                  chaptersLoading ||
                  chaptersFetching ||
                  !chapters.length
                }
                className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                + Add Section
              </button>
            </div>

            {form.sections.length === 0 && (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-5 py-10 text-center">
                <p className="text-sm font-medium text-gray-700">
                  No sections added
                </p>

                <p className="mt-1 text-xs text-gray-500">
                  Add a section to configure
                  the blueprint.
                </p>
              </div>
            )}

            <div className="space-y-4">
              {form.sections.map(
                (section, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-gray-200 bg-white"
                  >
                    {/* SECTION HEADER */}

                    <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
                          {index + 1}
                        </span>

                        <span className="text-sm font-semibold text-gray-900">
                          Section {index + 1}
                        </span>
                      </div>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            moveSection(
                              index,
                              "up"
                            )
                          }
                          disabled={
                            creating ||
                            index === 0
                          }
                          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs disabled:opacity-50"
                        >
                          ↑
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            moveSection(
                              index,
                              "down"
                            )
                          }
                          disabled={
                            creating ||
                            index ===
                              form.sections
                                .length -
                                1
                          }
                          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs disabled:opacity-50"
                        >
                          ↓
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            removeSection(
                              index
                            )
                          }
                          disabled={creating}
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    {/* SECTION BODY */}

                    <div className="space-y-4 p-4">

                      {/* NAME */}

                      <label className="block">
                        <span className="mb-1.5 block text-xs font-medium text-gray-700">
                          Section Name
                        </span>

                        <input
                          type="text"
                          value={
                            section.name
                          }
                          onChange={(event) =>
                            updateSection(
                              index,
                              {
                                name:
                                  event.target
                                    .value,
                              }
                            )
                          }
                          disabled={creating}
                          placeholder="Reading Comprehension"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm"
                        />
                      </label>

                      {/* CHAPTER + GROUP TYPE */}

                      <div className="grid gap-4 md:grid-cols-2">

                        <label className="block">
                          <span className="mb-1.5 block text-xs font-medium text-gray-700">
                            Chapter
                          </span>

                          <select
                            value={
                              section.chapterId
                            }
                            onChange={(event) =>
                              updateSection(
                                index,
                                {
                                  chapterId:
                                    event.target
                                      .value,
                                }
                              )
                            }
                            disabled={
                              creating ||
                              chaptersLoading ||
                              chaptersFetching
                            }
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                          >
                            <option value="">
                              Select Chapter
                            </option>

                            {chapters.map(
                              (chapter) => {
                                const id =
                                  getId(
                                    chapter
                                  );

                                if (!id) {
                                  return null;
                                }

                                return (
                                  <option
                                    key={id}
                                    value={id}
                                  >
                                    {chapter?.name ||
                                      chapter?.title ||
                                      chapter?.chapterName ||
                                      "Unnamed Chapter"}
                                  </option>
                                );
                              }
                            )}
                          </select>
                        </label>

                        <label className="block">
                          <span className="mb-1.5 block text-xs font-medium text-gray-700">
                            Group Type
                          </span>

                          <select
                            value={
                              section.groupType
                            }
                            onChange={(event) =>
                              updateSection(
                                index,
                                {
                                  groupType:
                                    event.target
                                      .value,
                                }
                              )
                            }
                            disabled={creating}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                          >
                            <option value="">
                              Select Group Type
                            </option>

                            {GROUP_TYPES.map(
                              (type) => (
                                <option
                                  key={
                                    type.value
                                  }
                                  value={
                                    type.value
                                  }
                                >
                                  {type.label}
                                </option>
                              )
                            )}
                          </select>
                        </label>

                      </div>

                      {/* GROUP COUNT */}

                      <label className="block max-w-sm">
                        <span className="mb-1.5 block text-xs font-medium text-gray-700">
                          Number of Groups
                        </span>

                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={
                            section.selectionCount
                          }
                          onChange={(event) =>
                            updateSection(
                              index,
                              {
                                selectionCount:
                                  Math.max(
                                    1,
                                    Number(
                                      event.target
                                        .value
                                    ) || 1
                                  ),
                              }
                            )
                          }
                          disabled={creating}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm"
                        />

                        <p className="mt-1 text-[11px] text-gray-500">
                          Number of complete groups
                          to select. Each group may
                          contain multiple questions.
                        </p>
                      </label>

                      {/* RANDOMIZATION */}

                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <p className="mb-3 text-xs font-semibold text-gray-800">
                          Options
                        </p>

                        <div className="grid gap-3 md:grid-cols-3">

                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={
                                section.randomizeGroups
                              }
                              onChange={(event) =>
                                updateSection(
                                  index,
                                  {
                                    randomizeGroups:
                                      event
                                        .target
                                        .checked,
                                  }
                                )
                              }
                              disabled={
                                creating
                              }
                              className="h-4 w-4"
                            />

                            <span className="text-xs">
                              Randomize Groups
                            </span>
                          </label>

                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={
                                section.randomizeQuestionsWithinGroup
                              }
                              onChange={(event) =>
                                updateSection(
                                  index,
                                  {
                                    randomizeQuestionsWithinGroup:
                                      event
                                        .target
                                        .checked,
                                  }
                                )
                              }
                              disabled={
                                creating
                              }
                              className="h-4 w-4"
                            />

                            <span className="text-xs">
                              Randomize Questions
                              Within Group
                            </span>
                          </label>

                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={
                                section.keepGroupTogether
                              }
                              onChange={(event) =>
                                updateSection(
                                  index,
                                  {
                                    keepGroupTogether:
                                      event
                                        .target
                                        .checked,
                                  }
                                )
                              }
                              disabled={
                                creating
                              }
                              className="h-4 w-4"
                            />

                            <span className="text-xs">
                              Keep Group Together
                            </span>
                          </label>

                        </div>
                      </div>

                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {/* SHUFFLE SECTIONS */}

          {form.sections.length > 0 && (
            <label className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <input
                type="checkbox"
                checked={
                  form.shuffleSections
                }
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    shuffleSections:
                      event.target.checked,
                  }))
                }
                disabled={creating}
                className="h-4 w-4"
              />

              <span className="text-xs font-semibold text-gray-800">
                Shuffle Sections
              </span>
            </label>
          )}

          {/* ERROR */}

          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-xs text-red-700">
                {formError}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ACTIONS */}

      <BlueprintActions
        onSave={handleSave}
        onReset={reset}
        onCancel={onCancel}
        saving={creating}
        showValidate={false}
        showReset
        showCancel={Boolean(onCancel)}
        saveLabel="Create Blueprint"
        resetLabel="Reset"
        cancelLabel="Cancel"
        sticky
      />

      {/* SUCCESS */}

      {createdBlueprint && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-xs font-semibold text-emerald-900">
            Blueprint created successfully.
          </p>

          {(createdBlueprint?.id ||
            createdBlueprint?._id ||
            createdBlueprint?.blueprintId) && (
            <p className="mt-1 font-mono text-[10px] text-emerald-700">
              {createdBlueprint?.id ||
                createdBlueprint?._id ||
                createdBlueprint?.blueprintId}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

EnglishQuizBlueprintCreate.propTypes = {
  quizId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  quiz: PropTypes.object,
  subjectId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  className: PropTypes.string,
};